import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import LanguageContext from "../../helper/LanguageContext";
import { $crud } from "../../helper/CrudFactory";
import MaterialIcon from "../MaterialIcon";
import ProjectPicker from "../ProjectPicker";
import { findExactProject } from "../../utils/projectMatch";
import { listAssignableRooms, setTeamsChatProject } from "../../helper/teamsApi";
import { TEAMS_PROJECT_EVENT } from "../../utils/teams/teamsChats";

/**
 * Files a Microsoft Teams chat under one of the user's project rooms, or removes that, via
 * PUT /teams/chats/:groupId/project. The daily report then lists the chat's messages under the
 * room; the chat itself stays room-less (room members never see it). Only existing rooms: the
 * type-ahead is the e-mail room picker (ProjectPicker) and reuses the .email-classify-* modal styles.
 *
 *   groupId   the Teams mirror group id
 *   project   the current room ({ id, name }) or null
 *   onSaved   (project | null) after the server took it; TEAMS_PROJECT_EVENT is fired as well
 */
export default function TeamsChatRoomModal({ groupId, project, onSaved, onClose }) {
  const { isGerm, strings } = React.useContext(LanguageContext);
  const tr = (key, de, en) => (strings && strings[key]) || (isGerm ? de : en);
  const [rooms, setRooms] = useState(null);
  const [name, setName] = useState(project ? project.name || "" : "");
  const [selected, setSelected] = useState(project || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listAssignableRooms().then((list) => {
      if (!cancelled) setRooms(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (next) => {
    if (busy || !groupId) return;
    setBusy(true);
    const r = await setTeamsChatProject(groupId, next ? next.id : null);
    setBusy(false);
    if (!r.ok) {
      const message = r.code === "room_not_assignable"
        ? tr("teams_room_not_assignable", "Dieses Projekt ist nicht verfügbar.", "This project is not available.")
        : r.message || strings.something_went_wrong;
      $crud.notify({ type: "error", message });
      return;
    }
    const saved = r.project || null;
    $crud.notify({
      type: "success",
      message: saved
        ? tr("teams_room_assigned", "Teams-Chat dem Projekt zugeordnet.", "Teams chat assigned to the project.")
        : tr("teams_room_removed", "Projektzuordnung entfernt.", "Project assignment removed."),
    });
    try {
      window.dispatchEvent(new CustomEvent(TEAMS_PROJECT_EVENT, { detail: { groupId: String(groupId), project: saved } }));
    } catch (_) { /* ignore */ }
    if (onSaved) onSaved(saved);
    if (onClose) onClose();
  };

  const submit = () => {
    const typed = name.trim();
    const pick = selected && selected.name === typed ? selected : findExactProject(typed, rooms || []);
    if (!pick) {
      $crud.notify({ type: "error", message: tr("teams_room_pick_existing", "Bitte wähle ein vorhandenes Projekt aus der Liste.", "Please pick an existing project from the list.") });
      return;
    }
    if (project && String(project.id) === String(pick.id)) {
      if (onClose) onClose();
      return;
    }
    save(pick);
  };

  const noRooms = Array.isArray(rooms) && rooms.length === 0;

  return ReactDOM.createPortal(
    <div className="email-classify-overlay email-classify-overlay--portal" role="dialog" aria-modal="true" onClick={busy ? undefined : onClose}>
      <div className="email-classify-modal" onClick={(e) => e.stopPropagation()}>
        <div className="email-classify-header">
          <div className="email-classify-title">
            <MaterialIcon icon="folder_special" size={20} color="#e066ed" />
            <span>{tr("teams_room_title", "Teams-Chat einem Projekt zuordnen", "Assign Teams chat to a project")}</span>
          </div>
          <button className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral" onClick={onClose} disabled={busy} title={strings.cancel || "Close"}>
            <MaterialIcon icon="close" size={18} />
          </button>
        </div>

        <div className="email-classify-section">
          <div className="email-classify-question">
            {tr(
              "teams_room_hint",
              "Nachrichten aus diesem Teams-Chat erscheinen dann im Tagesbericht unter dem Projekt.",
              "Messages from this Teams chat then appear in the daily report under the project."
            )}
          </div>
          {noRooms ? (
            <div className="email-classify-question">{tr("teams_room_no_rooms", "Du bist noch in keinem Projekt.", "You are not in any project yet.")}</div>
          ) : (
            <ProjectPicker
              value={name}
              buildings={rooms || []}
              placeholder={tr("teams_room_placeholder", "Projekt suchen", "Search project")}
              disabled={busy || rooms === null}
              autoFocus
              onChange={(v) => {
                setName(v);
                setSelected(null);
              }}
              onSelectExisting={(b) => {
                setName(b.name);
                setSelected({ id: b.id, name: b.name });
              }}
              onEnter={submit}
              strings={strings}
            />
          )}
          {!noRooms ? (
            <button
              className="kj-btn kj-btn--primary kj-btn--block email-classify-btn"
              onClick={submit}
              disabled={busy || rooms === null || !name.trim()}
            >
              <MaterialIcon icon="folder" size={16} />
              <span>{tr("teams_room_save", "Zuordnen", "Assign")}</span>
            </button>
          ) : null}
          {project ? (
            <button className="kj-btn kj-btn--secondary kj-btn--block email-classify-btn" onClick={() => save(null)} disabled={busy}>
              <MaterialIcon icon="folder_off" size={16} />
              <span>{tr("teams_room_remove", "Zuordnung entfernen", "Remove assignment")}</span>
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
